import { Embedder, type EmbedSettings } from '@source-digital/embed-core'
import { nanoid } from 'nanoid'
import { version } from '../../package.json'
import { merge } from 'lodash-es'

const defaultScope = 'experience'

export interface BrowserEmbedSettings extends EmbedSettings {
  /**
   * If true, load settings from the domain of the current page.
   * This is true by default. To disable, set to false.
   *
   * @default true
   */
  byDomain?: boolean

  /**
   * If true, initializes embedding with script URL's query parameters.
   */
  byUrl: boolean

  /**
   * If true, initializes embedding by reading <script type="application/json">...</script> content.
   * Pass a selector to find the script element.
   * If byUrl is true, this will be ignored.
   */
  byJsonElement?: string
  /**
   * If true, initializes embedding by reading settings from localStorage.
   * If string, it will be used as a key for localStorage.
   * If false, this will be ignored.
   * If true, the default key will be used (see {@link DEFAULT_LOCAL_STORAGE_KEY})
   */
  byLocalStorage?: string | boolean
  /**
   * A name of {@link BrowserEmbedder} in {@link window} obj
   */
  global?: string
  /**
   * If true, it will embed immediately.
   * If false, it will wait for `DOMContentLoaded` event. However, if DOM is already loaded, it will embed immediately.
   */
  immediate: boolean

  /**
   * If provided, the specified version of the embedder will be loaded
   * if it is not the version already running. Must be the exact version.
   * Semver is not supported
   */
  version?: string

  /**
   * If provided, the specified config will be loaded from the content service
   * @alias configId
   */
  config?: string

  /**
   * If provided, the specified config will be loaded from the content service
   * @alias config
   */
  configId?: string
}

/**
 * A storage for embedder instances. Designed for browser.
 * Designed to store multiple instances of {@link Embedder}
 */
export class BrowserEmbedder {
  #embedders: Record<string, Embedder> = {}
  version = version
  baseSettings?: EmbedSettings

  /**
   * Creates an instance of BrowserEmbedder.
   * If settings are passed to the constructor, they will be used as a base settings
   * for all registered embedders.
   * @param settings
   * @memberof BrowserEmbedder
   */
  constructor (settings?: EmbedSettings) {
    this.baseSettings = settings
  }

  /**
   * Registers a new {@link Embedder}.
   * {@link BrowserEmbedSettings.scope} must be unique.
   * @param settings
   * @returns created {@link Embedder}
   */
  register (settings: EmbedSettings): Embedder {
    settings.scope = settings.scope ?? nanoid()
    const mergedSettings = merge({}, this.baseSettings, settings)
    const embedder = new Embedder(mergedSettings)
    if (this.#embedders[embedder.context.settings.scope] != null) { throw new Error(`This embedder name is already taken: ${embedder.context.settings.scope ?? ''}`) }
    this.#embedders[embedder.context.settings.scope] = embedder
    return embedder
  }

  /**
   * @param scope
   * @returns an existing embedder with {@link BrowserEmbedSettings.scope}
   */
  get (scope: string): Embedder {
    return this.#embedders[scope ?? defaultScope]
  }

  /**
   *
   * @returns a list of all embedders
   */
  getAll (): Record<string, Embedder> {
    return this.#embedders
  }
}
